'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import {
  Check,
  ArrowRight,
  Award,
  FileText,
  Users,
  Clock,
  Shield,
  Globe,
  Home,
  Briefcase,
  Phone,
  Mail,
  MapPin,
} from 'lucide-react';
import ContactFormModal from '@/app/components/contact/contact-form-modal-pixxi';
import { useLanguage } from '@/app/context/language-context';
// import EnhancedContactForm from '@/app/components/ui/enhanced-contact-form';

function GoldenVisaClient() {
  const [isMounted, setIsMounted] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { t } = useLanguage();
  // const [showContactForm, setShowContactForm] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  if (!isMounted) return null;

  return (
    <div className='bg-white dark:bg-gray-950 text-gray-900 dark:text-white overflow-hidden'>
      {/* Hero Section */}
      <section className='relative min-h-[80vh] flex items-center justify-center overflow-hidden pt-20'>
        {/* Background with animated gradient */}
        <div className='absolute inset-0 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-gray-900 dark:to-gray-800 z-0'>
          <motion.div
            className='absolute inset-0 opacity-30 dark:opacity-40'
            animate={{
              background: [
                'radial-gradient(circle at 20% 30%, rgba(245, 158, 11, 0.15) 0%, rgba(249, 115, 22, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                'radial-gradient(circle at 80% 70%, rgba(249, 115, 22, 0.15) 0%, rgba(245, 158, 11, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                'radial-gradient(circle at 20% 30%, rgba(245, 158, 11, 0.15) 0%, rgba(249, 115, 22, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
              ],
            }}
            transition={{
              duration: 15,
              repeat: Number.POSITIVE_INFINITY,
              ease: 'easeInOut',
            }}
          />
        </div>

        {/* Decorative elements */}
        <div className='absolute inset-0 overflow-hidden z-0'>
          <motion.div
            className='absolute -top-20 -left-20 w-96 h-96 rounded-full bg-yellow-500/5 dark:bg-yellow-500/10'
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, 10, 0],
            }}
            transition={{
              repeat: Number.POSITIVE_INFINITY,
              duration: 20,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className='absolute -bottom-40 -right-40 w-[30rem] h-[30rem] rounded-full bg-orange-500/5 dark:bg-orange-500/10'
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, -10, 0],
            }}
            transition={{
              repeat: Number.POSITIVE_INFINITY,
              duration: 25,
              ease: 'easeInOut',
            }}
          />
        </div>

        <div className='container mx-auto px-4 z-10 py-20'>
          <div className='max-w-7xl mx-auto'>
            <motion.div
              className='text-center mb-16'
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.div
                className='inline-block px-6 py-2 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-full text-yellow-600 dark:text-orange-400 font-medium mb-6'
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                {t('uae_residency_solutions')}
              </motion.div>
              <motion.h1
                className='text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-yellow-600 to-orange-500 bg-clip-text text-transparent'
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {t('golden_visa_services')}
              </motion.h1>
              <motion.h2
                className='text-2xl md:text-3xl font-semibold mb-6 text-gray-700 dark:text-gray-300'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                {t('your_path')}
              </motion.h2>
              <motion.p
                className='text-xl text-gray-600 dark:text-gray-400 max-w-4xl mx-auto mb-8'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                {t('your_path_description')}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1 }}
                className='flex flex-col sm:flex-row justify-center gap-4'
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsContactModalOpen(true)}
                  className='px-8 py-4 bg-gradient-to-r from-yellow-600 to-orange-500 text-white rounded-full font-medium flex items-center justify-center group shadow-lg'
                >
                  {t('start_application')}
                  <ArrowRight className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1' />
                </motion.button>
                {/* <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className='px-8 py-4 bg-white dark:bg-gray-800 text-yellow-600 dark:text-orange-400 border-2 border-yellow-600 dark:border-orange-400 rounded-full font-medium hover:bg-yellow-600 hover:text-white dark:hover:bg-orange-400 dark:hover:text-black transition-colors shadow-lg'
                >
                  Free Consultation
                </motion.button> */}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Golden Visa Benefits Section */}
      <section className='py-20 px-4 bg-gray-50 dark:bg-gray-900'>
        <div className='container mx-auto'>
          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true }}
            variants={staggerContainer}
            className='text-center mb-16'
          >
            <motion.h2
              variants={fadeInUp}
              className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'
            >
              {t('uae_golden_visa_benefits')}
            </motion.h2>
            <motion.div
              variants={fadeInUp}
              className='w-24 h-1 bg-gradient-to-r from-yellow-600 to-orange-500 mx-auto mb-6'
            />
            <motion.p
              variants={fadeInUp}
              className='text-gray-700 dark:text-gray-300 text-lg max-w-4xl mx-auto'
            >
              {t('uae_golden_visa_benefits_description')}
            </motion.p>
          </motion.div>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {[
              {
                title: t('ten_year_residency'),
                description: t('ten_year_residency_description'),
                icon: <Clock className='h-8 w-8' />,
                color: 'from-yellow-500 to-yellow-600',
              },
              {
                title: t('family_inclusion'),
                description: t('family_inclusion_description'),
                icon: <Users className='h-8 w-8' />,
                color: 'from-orange-500 to-orange-600',
              },
              {
                title: t('no_sponsor_required'),
                description: t('no_sponsor_required_description'),
                icon: <Shield className='h-8 w-8' />,
                color: 'from-amber-500 to-amber-600',
              },
              {
                title: t('multiple_entry'),
                description: t('multiple_entry_description'),
                icon: <Globe className='h-8 w-8' />,
                color: 'from-yellow-600 to-orange-500',
              },
              {
                title: t('property_ownership'),
                description: t('property_ownership_description'),
                icon: <Home className='h-8 w-8' />,
                color: 'from-orange-600 to-red-500',
              },
              {
                title: t('business_opportunities'),
                description: t('business_opportunities_description'),
                icon: <Briefcase className='h-8 w-8' />,
                color: 'from-amber-600 to-yellow-500',
              },
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className='bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 group'
              >
                <div
                  className={`w-16 h-16 rounded-full bg-gradient-to-r ${benefit.color}/20 flex items-center justify-center mb-6 mx-auto text-white group-hover:scale-110 transition-transform`}
                >
                  <div
                    className={`w-12 h-12 rounded-full bg-gradient-to-r ${benefit.color} flex items-center justify-center`}
                  >
                    {benefit.icon}
                  </div>
                </div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-4 text-center'>
                  {benefit.title}
                </h3>
                <p className='text-gray-600 dark:text-gray-400 text-center'>
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility Criteria Section */}
      <section className='py-20 px-4'>
        <div className='container mx-auto'>
          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true }}
            variants={staggerContainer}
            className='text-center mb-16'
          >
            <motion.h2
              variants={fadeInUp}
              className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'
            >
              {t('golden_visa_eligibility_criteria')}
            </motion.h2>
            <motion.div
              variants={fadeInUp}
              className='w-24 h-1 bg-gradient-to-r from-yellow-600 to-orange-500 mx-auto mb-6'
            />
          </motion.div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
            {/* Property Investment Route */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className='bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700'
            >
              <div className='w-16 h-16 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 flex items-center justify-center mb-6 text-yellow-600 dark:text-orange-400'>
                <Home className='h-8 w-8' />
              </div>
              <h3 className='text-2xl font-bold text-gray-900 dark:text-white mb-4'>
                {t('property_investment_route')}
              </h3>
              <p className='text-gray-600 dark:text-gray-400 mb-6'>
                {t('property_investment_route_description')}
              </p>

              <div className='space-y-4'>
                <div className='flex items-start'>
                  <div className='w-6 h-6 ml-3 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center mr-3 mt-1'>
                    <Check className='w-6 h-4 text-white' />
                  </div>
                  <div>
                    <h4 className='font-semibold text-gray-900 dark:text-white'>
                      {t('min_investment')}
                    </h4>
                    <p className='text-gray-600 dark:text-gray-400 text-sm'>
                      {t('min_investment_description')}
                    </p>
                  </div>
                </div>
                <div className='flex items-start'>
                  <div className='w-6 h-6 ml-3 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center mr-3 mt-1'>
                    <Check className='w-6 h-4 text-white' />
                  </div>
                  <div>
                    <h4 className='font-semibold text-gray-900 dark:text-white'>
                      {t('freehold_properties')}
                    </h4>
                    <p className='text-gray-600 dark:text-gray-400 text-sm'>
                      {t('freehold_properties_description')}
                    </p>
                  </div>
                </div>
                <div className='flex items-start'>
                  <div className='w-6 h-6 ml-3 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center mr-3 mt-1'>
                    <Check className='w-6 h-4 text-white' />
                  </div>
                  <div>
                    <h4 className='font-semibold text-gray-900 dark:text-white'>
                      {t('no_loan_restrictions')}
                    </h4>
                    <p className='text-gray-600 dark:text-gray-400 text-sm'>
                      {t('no_loan_restrictions_description')}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Business Investment Route */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className='bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700'
            >
              <div className='w-16 h-16 rounded-full bg-gradient-to-r from-orange-500/20 to-red-500/20 flex items-center justify-center mb-6 text-orange-600 dark:text-red-400'>
                <Briefcase className='h-8 w-8' />
              </div>
              <h3 className='text-2xl font-bold text-gray-900 dark:text-white mb-4'>
                {t('business_investment_route')}
              </h3>
              <p className='text-gray-600 dark:text-gray-400 mb-6'>
                {t('business_investment_route_description')}
              </p>

              <div className='space-y-4'>
                <div className='flex items-start'>
                  <div className='w-6 h-6 ml-3 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center mr-3 mt-1'>
                    <Check className='w-6 h-4 text-white' />
                  </div>
                  <div>
                    <h4 className='font-semibold text-gray-900 dark:text-white'>
                      {t('business_investment_aed_10_million')}
                    </h4>
                    <p className='text-gray-600 dark:text-gray-400 text-sm'>
                      {t('business_investment_aed_10_million_description')}
                    </p>
                  </div>
                </div>
                <div className='flex items-start'>
                  <div className='w-6 h-6 ml-3 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center mr-3 mt-1'>
                    <Check className='w-6 h-4 text-white' />
                  </div>
                  <div>
                    <h4 className='font-semibold text-gray-900 dark:text-white'>
                      {t('specialized_talents')}
                    </h4>
                    <p className='text-gray-600 dark:text-gray-400 text-sm'>
                      {t('specialized_talents_description')}
                    </p>
                  </div>
                </div>
                <div className='flex items-start'>
                  <div className='w-6 h-6 ml-3 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center mr-3 mt-1'>
                    <Check className='w-6 h-4 text-white' />
                  </div>
                  <div>
                    <h4 className='font-semibold text-gray-900 dark:text-white'>
                      {t('outstanding_students')}
                    </h4>
                    <p className='text-gray-600 dark:text-gray-400 text-sm'>
                      {t('outstanding_students_description')}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className='py-20 px-4 bg-gray-50 dark:bg-gray-900'>
        <div className='container mx-auto'>
          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true }}
            variants={staggerContainer}
            className='text-center mb-16'
          >
            <motion.h2
              variants={fadeInUp}
              className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'
            >
              {/* Our Golden Visa Process */}
              {t('our_golden_visa_process')}
            </motion.h2>
            <motion.div
              variants={fadeInUp}
              className='w-24 h-1 bg-gradient-to-r from-yellow-600 to-orange-500 mx-auto mb-6'
            />
          </motion.div>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
            {[
              {
                step: '01',
                title: t('initial_consultation'),
                description: t('initial_consultation_description'),
                icon: <Users className='h-6 w-6' />,
              },
              {
                step: '02',
                title: t('property_selection'),
                description: t('property_selection_description'),
                icon: <Home className='h-6 w-6' />,
              },
              {
                step: '03',
                title: t('property_documentation'),
                description: t('property_documentation_description'),
                icon: <FileText className='h-6 w-6' />,
              },
              {
                step: '04',
                title: t('visa_approval'),
                description: t('visa_approval_description'),
                icon: <Award className='h-6 w-6' />,
              },
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className='relative bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300'
              >
                <div className='absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r from-yellow-600 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg'>
                  {process.step}
                </div>
                <div className='w-12 h-12 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 flex items-center justify-center mb-6 text-yellow-600 dark:text-orange-400'>
                  {process.icon}
                </div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-4'>
                  {process.title}
                </h3>
                <p className='text-gray-600 dark:text-gray-400'>
                  {process.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className='py-20 px-4'>
        <div className='container mx-auto'>
          <div className='flex flex-col lg:flex-row items-center gap-12 lg:gap-20'>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className='w-full lg:w-1/2'
            >
              <div className='relative'>
                <div className='absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-yellow-500 z-10'></div>
                <div className='absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-orange-500 z-10'></div>
                <div className='relative rounded-3xl overflow-hidden shadow-2xl'>
                  <div className='absolute inset-0 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 z-10 pointer-events-none'></div>
                  <Image
                    src='https://images.x-space.ae/assets/images/website_image/uae_golden_visa_consultation_meeting.webp'
                    alt='UAE Golden Visa Consultation'
                    width={600}
                    height={600}
                    className='w-full h-auto object-cover rounded-3xl hover:scale-105 transition-transform duration-700'
                  />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className='w-full lg:w-1/2 space-y-6'
            >
              <div className='inline-block px-6 py-2 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-full text-yellow-600 dark:text-orange-400 font-medium'>
                {t('expert_guidance')}
              </div>
              <h2 className='text-4xl font-bold text-gray-900 dark:text-white'>
                {t('expert_guidance_description')}
              </h2>
              <p className='text-lg text-gray-600 dark:text-gray-400'>
                {t('expert_guidance_description_1')}
              </p>

              <div className='space-y-4 pt-4'>
                {[
                  t('expert_guidance_description_2'),
                  t('expert_guidance_description_3'),
                  t('expert_guidance_description_4'),
                  t('expert_guidance_description_5'),
                  t('expert_guidance_description_6'),
                  t('expert_guidance_description_7'),
                  t('expert_guidance_description_8'),
                  t('expert_guidance_description_9'),
                ].map((service, index) => (
                  <div key={index} className='flex items-center'>
                    <div className='w-6 h-6 ml-3 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center mr-3'>
                      <Check className='w-6 h-4 text-white' />
                    </div>
                    <span className='text-gray-700 dark:text-gray-300'>
                      {service}
                    </span>
                  </div>
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsContactModalOpen(true)}
                className='mt-6 px-8 py-3 bg-gradient-to-r from-yellow-600 to-orange-500 text-white rounded-full font-medium flex items-center group'
              >
                {t('start_application')}
                <ArrowRight className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1' />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className='py-20 px-4 bg-gray-50 dark:bg-gray-900'>
        <div className='container mx-auto'>
          <div className='relative overflow-hidden rounded-3xl bg-gradient-to-r from-yellow-500/10 to-orange-500/10 dark:from-yellow-500/20 dark:to-orange-500/20 shadow-xl'>
            <div className='absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-yellow-500 rounded-tl-3xl z-10'></div>
            <div className='absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-orange-500 rounded-tr-3xl z-10'></div>
            <div className='absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-yellow-500 rounded-bl-3xl z-10'></div>
            <div className='absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-orange-500 rounded-br-3xl z-10'></div>

            <div className='p-12 relative z-10 text-center'>
              <motion.h2
                className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                {t('ready_to_secure')}
              </motion.h2>
              <motion.p
                className='text-lg text-gray-700 dark:text-gray-300 mb-8 max-w-3xl mx-auto'
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                {t('ready_to_secure_description')}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
                className='flex flex-col sm:flex-row justify-center gap-4 mb-8'
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsContactModalOpen(true)}
                  className='px-8 py-4 bg-gradient-to-r from-yellow-600 to-orange-500 text-white rounded-full font-medium hover:shadow-xl transition-shadow'
                >
                  {t('start_application')}
                </motion.button>
                {/* <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className='px-8 py-4 bg-white dark:bg-gray-800 text-yellow-600 dark:text-orange-400 border-2 border-yellow-600 dark:border-orange-400 rounded-full font-medium hover:bg-yellow-600 hover:text-white dark:hover:bg-orange-400 dark:hover:text-black transition-colors shadow-lg'
                >
                  Free Consultation
                </motion.button> */}
              </motion.div>

              <div className='grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-10'>
                {/* Phone (two numbers) */}
                <div className='flex items-center justify-center space-x-3'>
                  <Phone className='h-5 w-5 text-yellow-600 dark:text-orange-400' />
                  <div className='flex items-center space-x-2'>
                    <a
                      href='tel:04-3965199'
                      className='text-gray-700 dark:text-gray-300 hover:text-yellow-600 dark:hover:text-orange-400'
                    >
                      04-3965199
                    </a>
                    <span>|</span>
                    <a
                      href='tel:800-9889'
                      className='text-gray-700 dark:text-gray-300 hover:text-yellow-600 dark:hover:text-orange-400'
                    >
                      800-9889
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className='flex items-center justify-center space-x-3'>
                  <Mail className='h-5 w-5 text-yellow-600 dark:text-orange-400' />
                  <a
                    href='mailto:info@x-space.ae'
                    className='text-gray-700 dark:text-gray-300 hover:text-yellow-600 dark:hover:text-orange-400'
                  >
                    info@x-space.ae
                  </a>
                </div>

                {/* Location */}
                <div className='flex items-center justify-center space-x-3'>
                  <MapPin className='h-5 w-5 text-yellow-600 dark:text-orange-400' />
                  <a
                    href='https://maps.app.goo.gl/ypptzFUFJ9HRryow5'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-gray-700 dark:text-gray-300 hover:text-yellow-600 dark:hover:text-orange-400'
                  >
                    Dubai, UAE
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {isContactModalOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto'>
            <div className='flex justify-between items-center mb-6'>
              <h3 className='text-2xl font-bold text-gray-900 dark:text-white'>
                {t('golden_visa_consultation')}
              </h3>
              <button
                onClick={() => setIsContactModalOpen(false)}
                className='text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              >
                <svg
                  className='w-6 h-6'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>
            {/* Contact Form Modal */}
            <ContactFormModal
              isOpen={isContactModalOpen}
              onClose={() => setIsContactModalOpen(false)}
              title='Website Lead - Get Your Free Property Valuation for Golden Visa Eligibility'
              subtitle='Fill out the form below and our property experts will contact you within 24 hours with a comprehensive market analysis.'
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default GoldenVisaClient;
